import { useRef, useState } from 'react';

import { useDialogMutation } from 'hooks/useDialogMutation';
import { getAuthSessionRevision, isAuthSessionCurrent } from 'services/auth/session';

export function useSavedRemoval<T>(
  isOpen: boolean,
  onRecovered: (data: T) => void,
  context: string,
): {
  isLoading: boolean;
  needsRecovery: boolean;
  errMessage: string;
  run: (
    remove: (signal: AbortSignal) => Promise<void>,
    markModified: (signal: AbortSignal) => Promise<unknown>,
    read: (signal: AbortSignal) => Promise<T>,
    onRemoved: () => void,
  ) => Promise<void>;
} {
  const saved = useRef(false);
  const [needsRecovery, setNeedsRecovery] = useState(false);
  const action = useDialogMutation<T>(onRecovered, context, undefined, isOpen);
  const run = (
    remove: (signal: AbortSignal) => Promise<void>,
    markModified: (signal: AbortSignal) => Promise<unknown>,
    read: (signal: AbortSignal) => Promise<T>,
    onRemoved: () => void,
  ): Promise<void> =>
    action.run(async signal => {
      const revision = getAuthSessionRevision();
      const ensureCurrent = (): void => {
        if (signal.aborted || !isAuthSessionCurrent(revision))
          throw new Error('Removal context changed');
      };
      if (!saved.current) {
        await remove(signal);
        ensureCurrent();
        saved.current = true;
        setNeedsRecovery(true);
        onRemoved();
        // This existing metadata update is attempted once. Acknowledged removal is
        // never replayed, and a manual recovery only reads the saved information.
        await markModified(signal);
        ensureCurrent();
      }
      ensureCurrent();
      const data = await read(signal);
      ensureCurrent();
      return data;
    });
  return {
    isLoading: action.isLoading,
    needsRecovery,
    errMessage: action.errMessage,
    run,
  };
}
